// Product languages, shared by the fronts and the server (no dependency).
// French is the source language: every other dictionary is typed against it.

export const LOCALES = ["fr", "en"] as const;
export type Locale = (typeof LOCALES)[number];

export const DEFAULT_LOCALE: Locale = "fr";

/** Guest language, set by the app on its own host: read by the API for requests without an account. */
export const LOCALE_COOKIE = "hune-locale";

/** `Intl` locale of each language (dates, numbers, currencies). */
export const INTL_LOCALE: Record<Locale, string> = { fr: "fr-FR", en: "en-US" };

/** Each language named in itself (language picker). */
export const LOCALE_NAMES: Record<Locale, string> = { fr: "Français", en: "English" };

export function isLocale(value: unknown): value is Locale {
  return typeof value === "string" && (LOCALES as readonly string[]).includes(value);
}

/**
 * First supported language in a list of language tags, in order of
 * preference: `en-GB`, `fr`, or an `Accept-Language` header
 * (`en-US,en;q=0.9,fr;q=0.8`). `null` when none matches.
 */
export function matchLocale(value: string | readonly string[] | null | undefined): Locale | null {
  if (!value) return null;
  const tags =
    typeof value === "string"
      ? value
          .split(",")
          .map((part) => {
            const [tag, ...params] = part.trim().split(";");
            const quality = params.find((param) => param.trim().startsWith("q="));
            return { tag, q: quality ? Number(quality.trim().slice(2)) || 0 : 1 };
          })
          .filter((entry) => entry.q > 0)
          .sort((a, b) => b.q - a.q)
          .map((entry) => entry.tag)
      : value;
  for (const tag of tags) {
    const base = tag.trim().toLowerCase().split("-")[0];
    if (isLocale(base)) return base;
  }
  return null;
}
